import {
  type CampaignCreationCapabilities,
  isSmartCreationNegativeKeywordWithinWordLimit,
} from "@adpilot/shared";
import { IconPlus } from "@tabler/icons-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoneyInput } from "@/components/patterns/MoneyInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { QuickTargetValue } from "./review-quick-targets";

interface ReviewQuickAddTargetsProps {
  negative: boolean;
  targetType: "keyword" | "product";
  match: "broad" | "phrase" | "exact" | "expanded";
  defaultBid: number;
  bidLimits?: CampaignCreationCapabilities["bid"];
  disabled: boolean;
  onAdd(values: QuickTargetValue[]): { added: number; skipped: number; error?: string };
}

/** The composer adds to the Sheet's staging area; its footer remains the save boundary. */
export function ReviewQuickAddTargets({
  negative,
  targetType,
  match,
  defaultBid,
  bidLimits,
  disabled,
  onAdd,
}: ReviewQuickAddTargetsProps) {
  const { t } = useTranslation("smartCreation");
  const id = useId();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<"keyword" | "product">("keyword");
  const [negativeMatch, setNegativeMatch] = useState<"NEGATIVE_EXACT" | "NEGATIVE_PHRASE">(
    "NEGATIVE_EXACT",
  );
  const [text, setText] = useState("");
  const [bid, setBid] = useState(String(defaultBid));
  const [issues, setIssues] = useState<{ input?: string; bid?: string }>({});
  const [notice, setNotice] = useState<string | null>(null);
  const actualType = negative ? type : targetType;
  const label = t(actualType === "product" ? "review.quickAdd.asins" : "review.quickAdd.keywords");
  const min = bidLimits?.min ?? 0.01;
  const max = bidLimits?.max ?? 1_000_000;
  const decimals = bidLimits?.decimals ?? 2;

  function submit() {
    if (disabled) return;
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim().replace(/\s+/g, " "))
      .filter(Boolean);
    const next: typeof issues = {};
    if (!lines.length || lines.length > 100) next.input = t("review.quickAdd.invalidList");
    else if (actualType === "product" && lines.some((line) => !/^[a-z0-9]{10}$/i.test(line)))
      next.input = t("review.quickAdd.invalidAsin");
    else if (actualType === "keyword" && lines.some((line) => line.length > 80))
      next.input = t("review.quickAdd.invalidKeyword");
    else if (
      negative &&
      actualType === "keyword" &&
      lines.some((line) => !isSmartCreationNegativeKeywordWithinWordLimit(line, negativeMatch))
    )
      next.input = t("review.quickAdd.invalidNegativeKeyword", {
        words: negativeMatch === "NEGATIVE_PHRASE" ? 4 : 10,
      });
    const amount = Number(bid);
    const units = amount * 10 ** decimals;
    if (
      !negative &&
      (bid.trim() === "" ||
        !Number.isFinite(amount) ||
        amount < min ||
        amount > max ||
        Math.abs(units - Math.round(units)) > 1e-7)
    )
      next.bid = t("review.quickAdd.invalidBid", { min, max, decimals });
    setIssues(next);
    setNotice(null);
    if (Object.keys(next).length) return;
    const result = onAdd(
      lines.map((line) => ({
        label: actualType === "product" ? line.toUpperCase() : line,
        type: actualType,
        match: negative ? negativeMatch : match,
        bid: negative ? 0 : amount,
      })),
    );
    if (result.error) {
      setIssues({ input: result.error });
      return;
    }
    if (result.skipped) {
      setNotice(t("review.quickAdd.duplicates", result));
      if (result.added) setText("");
    } else setOpen(false);
  }

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(next) => {
        if (next && disabled) return;
        if (next) {
          setText("");
          setBid(String(defaultBid));
          setType("keyword");
          setNegativeMatch("NEGATIVE_EXACT");
          setIssues({});
          setNotice(null);
        }
        setOpen(next);
      }}
    >
      <PopoverTrigger render={<Button size="sm" variant="outline" disabled={disabled} />}>
        <IconPlus data-icon="inline-start" />
        {t(negative ? "review.quickAdd.negatives" : "review.quickAdd.targets")}
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        sideOffset={8}
        initialFocus={inputRef}
        className="smart-creation-preview w-[360px] [&>[data-slot=popover-viewport]]:p-3"
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <PopoverTitle className="sr-only">
              {t(negative ? "review.quickAdd.titleNegatives" : "review.quickAdd.titleTargets")}
            </PopoverTitle>
            {!negative ? <Badge variant="secondary">{t(`review.match.${match}`)}</Badge> : null}
          </div>
          <FieldGroup className="gap-3">
            {negative ? (
              <div className="flex gap-3">
                <Field className="min-w-0 flex-1">
                  <FieldLabel
                    htmlFor={`${id}-type`}
                    className="text-xs"
                    style={{ lineHeight: "16px" }}
                  >
                    {t("review.quickAdd.negativeType")}
                  </FieldLabel>
                  <Select
                    value={type}
                    onValueChange={(value) => {
                      setType(value === "product" ? "product" : "keyword");
                      setText("");
                      setIssues({});
                      setNotice(null);
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      id={`${id}-type`}
                      size="sm"
                      aria-label={t("review.quickAdd.negativeType")}
                    >
                      <SelectValue>
                        {t(
                          type === "product"
                            ? "review.quickAdd.negativeProduct"
                            : "review.quickAdd.negativeKeyword",
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="keyword">
                          {t("review.quickAdd.negativeKeyword")}
                        </SelectItem>
                        <SelectItem value="product">
                          {t("review.quickAdd.negativeProduct")}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
                {type === "keyword" ? (
                  <Field className="min-w-0 flex-1">
                    <FieldLabel
                      htmlFor={`${id}-match`}
                      className="text-xs"
                      style={{ lineHeight: "16px" }}
                    >
                      {t("review.quickAdd.negativeMatch")}
                    </FieldLabel>
                    <Select
                      value={negativeMatch}
                      onValueChange={(value) => {
                        setNegativeMatch(
                          value === "NEGATIVE_PHRASE" ? "NEGATIVE_PHRASE" : "NEGATIVE_EXACT",
                        );
                        setNotice(null);
                      }}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        id={`${id}-match`}
                        size="sm"
                        aria-label={t("review.quickAdd.negativeMatch")}
                      >
                        <SelectValue>
                          {t(
                            negativeMatch === "NEGATIVE_PHRASE"
                              ? "review.match.negativePhrase"
                              : "review.match.negativeExact",
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="NEGATIVE_EXACT">
                            {t("review.match.negativeExact")}
                          </SelectItem>
                          <SelectItem value="NEGATIVE_PHRASE">
                            {t("review.match.negativePhrase")}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </Field>
                ) : null}
              </div>
            ) : null}
            <Field invalid={Boolean(issues.input)} className="gap-1.5">
              <FieldLabel
                htmlFor={`${id}-input`}
                className="text-xs"
                style={{ lineHeight: "16px" }}
              >
                {label}
              </FieldLabel>
              <Textarea
                ref={inputRef}
                id={`${id}-input`}
                aria-label={label}
                aria-invalid={Boolean(issues.input)}
                value={text}
                disabled={disabled}
                rows={3}
                className="h-24 [&_textarea]:h-full [&_textarea]:resize-none [&_textarea]:py-2.5 [&_textarea]:text-[13px]"
                placeholder={t(
                  actualType === "product"
                    ? "review.quickAdd.asinPlaceholder"
                    : "review.quickAdd.keywordPlaceholder",
                )}
                onChange={(event) => {
                  setText(event.target.value);
                  setIssues((current) => ({ ...current, input: undefined }));
                  setNotice(null);
                }}
                onKeyDown={(event) => {
                  if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                    event.preventDefault();
                    submit();
                  }
                }}
              />
              <FieldDescription className="leading-[18px]">
                {t(
                  actualType === "product"
                    ? "review.quickAdd.asinHint"
                    : negative
                      ? "review.quickAdd.negativeHint"
                      : "review.quickAdd.keywordHint",
                )}
              </FieldDescription>
              {issues.input ? <FieldError match>{issues.input}</FieldError> : null}
            </Field>
            {!negative ? (
              <Field invalid={Boolean(issues.bid)}>
                <div className="flex w-full items-center justify-between gap-3">
                  <FieldLabel htmlFor={`${id}-bid`} className="text-xs">
                    {t("review.quickAdd.bid")}
                  </FieldLabel>
                  <MoneyInput
                    id={`${id}-bid`}
                    aria-label={t("review.quickAdd.bid")}
                    aria-invalid={Boolean(issues.bid)}
                    type="number"
                    min={min}
                    max={max}
                    step={10 ** -decimals}
                    value={bid}
                    disabled={disabled}
                    containerClassName="h-8 w-26 [&_input]:h-full"
                    onChange={(event) => {
                      setBid(event.target.value);
                      setIssues((current) => ({ ...current, bid: undefined }));
                    }}
                  />
                </div>
                {issues.bid ? <FieldError match>{issues.bid}</FieldError> : null}
              </Field>
            ) : null}
          </FieldGroup>
          {notice ? (
            <p role="status" className="text-xs leading-5 text-muted-foreground">
              {notice}
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
                {t("review.quickAdd.cancel")}
              </Button>
              <Button
                size="sm"
                type="submit"
                disabled={disabled || !text.trim()}
                aria-keyshortcuts="Control+Enter Meta+Enter"
              >
                {t("review.quickAdd.add")}
              </Button>
            </div>
            <p className="text-xs leading-[18px] text-muted-foreground">
              {t("review.quickAdd.saveHint")}
            </p>
          </div>
        </form>
      </PopoverPopup>
    </Popover>
  );
}
